import { create } from 'zustand';
import {
  applyBillDiscount,
  computeTotals,
  priceLine,
  removeTax,
  taxBucketsFor,
  taxReliefOf,
  type BillDiscount,
  type LineDiscount,
  type PricedLine,
  type RoundOffMode,
  type SaleTotals,
  type TaxRate,
  type TaxRemovalMode,
} from '@sp/core';
import { OPEN_ITEM_PRODUCT_ID, OPEN_ITEM_SKU } from '@sp/core';
import type { PosProduct } from '../lib/types';
import { tr } from '../i18n';

/**
 * The cart.
 *
 * Totals are computed with the SAME pricing and tax functions the server uses, from
 * `@sp/core`. That is deliberate: if the till showed its own arithmetic, the number on
 * screen could disagree with the invoice, and a cashier who does not trust the screen
 * cannot take a payment. One engine, one answer.
 *
 * All money is paise and all quantity is milli-units, matching the database. `qtyMilli`
 * is what gets sent to the API; nothing here ever multiplies floats.
 */

export type DiscountKind = 'none' | 'percent' | 'amount';

export interface CartLine {
  /** Stable key: product + batch + rate + discount. Same key merges into one line. */
  key: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  barcode: string | null;
  qtyMilli: number;
  ratePaise: number;
  mrpPaise: number;
  discountKind: DiscountKind;
  /** percent: basis points. amount: paise per unit. */
  discountValue: number;
  discountPerUnit: boolean;
  taxRateId: string;
  gstBps: number;
  cessBps: number;
  pricingInclusive: boolean;
  hsnCode: string | null;
  allowDecimalQty: boolean;
  stockMilli: number;
  batchId: string | null;
  batchNumber: string | null;
  isExpiryTracked: boolean;
  /** Marathi / Hindi shelf name, shown under the English one. */
  localName: string | null;
  /** "Vegetables/Fresh Cut": the aisle, which picks the line's colour. */
  categoryName: string | null;
  /** Expiry of the stock this line sells from, so the cashier sees a short-dated item before the customer does. */
  expiryDate: string | null;
  /**
   * Set on a line typed at the till (the open item): a per-line id so two identical open items never
   * merge, the typed name, and the Item Tax rate chosen for it (null = no tax).
   */
  openItem?: { id: string; name: string; taxRateId: string | null } | null;
}

/** A tax rate as the Item Tax picker offers it. */
export interface OpenItemRate {
  id: string;
  name: string;
  gstBps: number;
  cessBps: number;
}

/** What the till shows about the customer on the bill: enough to greet a regular, nothing more. */
export interface CustomerSummary {
  id: string;
  name: string;
  phone: string;
  visitCount: number | null;
  totalSpendPaise: number | null;
  lastVisitAt: string | null;
}

interface CartState {
  generation: number;
  lines: CartLine[];
  billDiscount: BillDiscount;
  customerId: string | null;
  customerName: string | null;
  customerPhone: string | null;
  customer: CustomerSummary | null;
  /** The line the last scan or tap went to, and how much it added, so it can be confirmed and undone. */
  lastAdded: { key: string; qtyMilli: number } | null;
  /**
   * The customer's GST state code. Inter-state supply is IGST instead of CGST+SGST, so the
   * till has to know this to show the right tax split before the sale is submitted. The
   * customer object carries it, so it is cached here rather than re-fetched per render.
   */
  customerStateCode: string | null;
  note: string | null;
  /**
   * "No Tax" on this bill, recorded the way the store's setting says; null when off. Kept with the
   * bill (not read from config at pricing time) so a held bill resumes priced as it was held.
   */
  taxExempt: TaxRemovalMode | null;
  taxExemptNote: string | null;
  /** Set when a scan could not be added, so the till can flash a reason. */
  lastRejection: { message: string; at: number } | null;

  addProduct: (product: PosProduct, qtyMilli?: number) => void;
  addBatchLine: (product: PosProduct, batchId: string | null, batchNumber: string | null, expiryDate?: string | null) => void;
  /** Add a weighed or typed quantity on top of whatever is already on the bill for this product. */
  addQuantity: (product: PosProduct, qtyMilli: number) => void;
  /** Take back exactly what the last scan or tap added. */
  undoLastAdd: () => void;
  setQty: (key: string, qtyMilli: number) => void;
  increment: (key: string, stepMilli: number) => void;
  setRate: (key: string, ratePaise: number) => void;
  setLineDiscount: (key: string, kind: DiscountKind, value: number, perUnit?: boolean) => void;
  setBillDiscount: (discount: BillDiscount) => void;
  removeLine: (key: string) => void;
  clear: () => void;
  attachCustomer: (
    customer:
      | {
          id: string;
          name: string;
          phone: string;
          stateCode?: string | null;
          visitCount?: number;
          totalSpendPaise?: number;
          lastVisitAt?: string | null;
        }
      | null,
  ) => void;
  setNote: (note: string | null) => void;
  setTaxExempt: (mode: TaxRemovalMode | null, note?: string | null) => void;
  /** Add a line typed at the till. `noTax` is the rate used until Item Tax picks another. */
  addOpenItem: (input: { name: string; ratePaise: number; qtyMilli: number; pricingInclusive: boolean; noTax: OpenItemRate }) => void;
  /** Item Tax on one open-item line; null takes the tax off again. */
  setOpenItemTax: (key: string, rate: OpenItemRate | null, noTax: OpenItemRate) => void;
  reject: (message: string) => void;
  loadLines: (lines: CartLine[]) => void;
  /**
   * Resume a held bill: load its lines and set-or-clear the customer in ONE state
   * update, so a walk-in hold can never inherit the previous bill's customer (and its
   * GST state code). Prefer this over loadLines + attachCustomer for hold/resume.
   */
  loadHeldBill: (lines: CartLine[], customer: HeldCustomer | null) => void;
}

/** Customer identity as carried on a held bill: enough to price and attribute, nothing more. */
export interface HeldCustomer {
  id: string;
  name: string;
  phone: string;
  stateCode?: string | null;
  visitCount?: number;
  totalSpendPaise?: number;
  lastVisitAt?: string | null;
}

function lineKey(input: {
  productId: string;
  batchId: string | null;
  ratePaise: number;
  discountKind: DiscountKind;
  discountValue: number;
  discountPerUnit?: boolean;
  openItem?: { id: string } | null;
}): string {
  // An open item is its own line whatever it shares with another: same name and price stay two lines.
  if (input.openItem) return ['open', input.openItem.id, input.ratePaise, input.discountKind, input.discountValue].join(':');
  return [
    input.productId,
    input.batchId ?? '-',
    input.ratePaise,
    input.discountKind,
    input.discountValue,
    input.discountPerUnit === true ? 'u' : 't',
  ].join(':');
}

function toLine(product: PosProduct, batchId: string | null, batchNumber: string | null, expiryDate: string | null = null): CartLine {
  return {
    key: '',
    productId: product.id,
    productName: product.name,
    sku: product.sku,
    unit: product.unit,
    barcode: product.barcode,
    qtyMilli: 0,
    ratePaise: product.sellingPricePaise,
    mrpPaise: product.mrpPaise,
    discountKind: 'none',
    discountValue: 0,
    discountPerUnit: false,
    taxRateId: product.taxRateId,
    gstBps: product.gstBps,
    cessBps: product.cessBps,
    pricingInclusive: product.pricingInclusive,
    hsnCode: product.hsnCode,
    allowDecimalQty: product.allowDecimalQty,
    stockMilli: product.stockMilli,
    batchId,
    batchNumber,
    isExpiryTracked: product.isExpiryTracked,
    localName: product.localName,
    categoryName: product.categoryName,
    // The batch FEFO picked, else the earliest expiry on the shelf: the stock this sale draws first.
    expiryDate: expiryDate ?? product.nextExpiryDate,
  };
}

/**
 * The step for "+"/keyboard increment. Counted items step by 1; weighed goods step by 50 g
 * so the cashier can reach 1.250 kg without typing, which is how a scale-integrated POS
 * behaves.
 */
/**
 * Quantity added per tap/scan, in milli-units. A counted item steps by ONE piece (1000 milli),
 * not by 1 milli — that added 0.001 pcs and billed ₹0. Loose goods step by 50 g / 50 ml.
 */
export function stepFor(line: Pick<CartLine, 'allowDecimalQty'>): number {
  return line.allowDecimalQty ? 50 : 1000;
}

function taxRateOf(line: CartLine): TaxRate {
  return {
    id: line.taxRateId,
    name: '',
    gstBps: line.gstBps,
    cessBps: line.cessBps,
    isActive: true,
  };
}

const CART_STORAGE_KEY = 'sp.cart.v1';

function readPersistedCart(): Partial<Pick<CartState, 'lines' | 'billDiscount' | 'customerId' | 'customerName' | 'customerPhone' | 'customer' | 'customerStateCode' | 'note' | 'taxExempt' | 'taxExemptNote'>> {
  try {
    if (typeof sessionStorage === 'undefined') return {};
    const raw = sessionStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || !Array.isArray(parsed.lines)) return {};
    // Lines carry only plain data (no functions); validate lightly and drop the bill
    // rather than resume a corrupt one.
    const lines = (parsed.lines as CartLine[]).filter(
      (line) => line && typeof line.productId === 'string' && Number.isInteger(line.qtyMilli) && line.qtyMilli > 0,
    );
    return {
      lines,
      billDiscount: (parsed.billDiscount as CartState['billDiscount']) ?? { type: 'none' },
      customerId: (parsed.customerId as string | null) ?? null,
      customerName: (parsed.customerName as string | null) ?? null,
      customerPhone: (parsed.customerPhone as string | null) ?? null,
      customer: (parsed.customer as CartState['customer']) ?? null,
      customerStateCode: (parsed.customerStateCode as string | null) ?? null,
      note: (parsed.note as string | null) ?? null,
      taxExempt: (parsed.taxExempt as CartState['taxExempt']) ?? null,
      taxExemptNote: (parsed.taxExemptNote as string | null) ?? null,
    };
  } catch {
    return {};
  }
}

export const useCart = create<CartState>((set, get) => ({
  generation: 0,
  lines: [],
  billDiscount: { type: 'none' },
  customerId: null,
  customerName: null,
  customerPhone: null,
  customer: null,
  lastAdded: null,
  customerStateCode: null,
  note: null,
  taxExempt: null,
  taxExemptNote: null,
  lastRejection: null,
  ...readPersistedCart(),

  addProduct(product, qtyMilli) {
    // An explicit quantity (grid tap "3 milk", scale 1.425 kg) adds on top of what is
    // already on the bill. Replacing would silently drop the earlier quantity, and
    // undo would then delete stock the cashier had before.
    if (qtyMilli !== undefined && qtyMilli > 0) {
      get().addQuantity(product, qtyMilli);
      return;
    }
    get().addBatchLine(product, null, null);
  },

  addBatchLine(product, batchId, batchNumber, expiryDate = null) {
    const line = toLine(product, batchId, batchNumber, expiryDate);
    const key = lineKey({
      productId: line.productId,
      batchId,
      ratePaise: line.ratePaise,
      discountKind: line.discountKind,
      discountValue: line.discountValue,
    });
    const { lines, reject } = get();
    const existing = lines.find((entry) => entry.key === key);
    const step = stepFor(line);

    if (existing) {
      set((state) => ({
        lines: state.lines.map((entry) => (entry.key === key ? { ...entry, qtyMilli: entry.qtyMilli + step } : entry)),
        lastAdded: { key, qtyMilli: step },
        lastRejection: null,
      }));
      return;
    }

    // A withdrawn product can never be sold. Stock on hand is not enforced here: the server's
    // `billing.allow_negative_stock` setting (and a cashier's inventory.adjust permission) is the
    // authority on negative stock, so a hard block at the till would contradict the store config.
    if (!product.isActive) {
      reject(tr('pos.withdrawn', { name: product.name }));
      return;
    }

    set((state) => ({
      lines: [...state.lines, { ...line, key, qtyMilli: step }],
      lastAdded: { key, qtyMilli: step },
      lastRejection: null,
    }));
  },

  addQuantity(product, qtyMilli) {
    if (!(qtyMilli > 0)) return;
    const line = toLine(product, null, null);
    const key = lineKey({ productId: line.productId, batchId: null, ratePaise: line.ratePaise, discountKind: 'none', discountValue: 0 });
    set((state) => {
      const existing = state.lines.some((entry) => entry.key === key);
      return {
        lines: existing
          ? state.lines.map((entry) => (entry.key === key ? { ...entry, qtyMilli: entry.qtyMilli + qtyMilli } : entry))
          : [...state.lines, { ...line, key, qtyMilli }],
        lastAdded: { key, qtyMilli },
        lastRejection: null,
      };
    });
  },

  undoLastAdd() {
    const { lastAdded, lines } = get();
    if (!lastAdded) return;
    const line = lines.find((entry) => entry.key === lastAdded.key);
    if (!line) {
      set({ lastAdded: null });
      return;
    }
    const remaining = line.qtyMilli - lastAdded.qtyMilli;
    set((state) => ({
      lines:
        remaining > 0
          ? state.lines.map((entry) => (entry.key === line.key ? { ...entry, qtyMilli: remaining } : entry))
          : state.lines.filter((entry) => entry.key !== line.key),
      lastAdded: null,
    }));
  },

  setQty(key, qtyMilli) {
    if (qtyMilli <= 0) {
      get().removeLine(key);
      return;
    }
    set((state) => ({
      lines: state.lines.map((entry) => (entry.key === key ? { ...entry, qtyMilli } : entry)),
    }));
  },

  increment(key, stepMilli) {
    set((state) => ({
      lines: state.lines.map((entry) =>
        entry.key === key ? { ...entry, qtyMilli: entry.qtyMilli + stepMilli } : entry,
      ),
    }));
  },

  setRate(key, ratePaise) {
    set((state) => ({
      lines: state.lines.map((entry) =>
        // The key encodes the rate, so a re-rate has to produce a new key or two lines
        // with the same product and different rates would silently merge.
        entry.key === key
          ? {
              ...entry,
              ratePaise,
              key: lineKey({
                productId: entry.productId,
                batchId: entry.batchId,
                ratePaise,
                discountKind: entry.discountKind,
                discountValue: entry.discountValue,
                discountPerUnit: entry.discountPerUnit,
                openItem: entry.openItem,
              }),
            }
          : entry,
      ),
    }));
  },

  setLineDiscount(key, kind, value, perUnit = false) {
    set((state) => ({
      lines: state.lines.map((entry) =>
        entry.key === key
          ? {
              ...entry,
              discountKind: kind,
              discountValue: value,
              discountPerUnit: perUnit,
              key: lineKey({
                productId: entry.productId,
                batchId: entry.batchId,
                ratePaise: entry.ratePaise,
                discountKind: kind,
                discountValue: value,
                discountPerUnit: perUnit,
                openItem: entry.openItem,
              }),
            }
          : entry,
      ),
    }));
  },

  setBillDiscount(discount) {
    set({ billDiscount: discount });
  },

  removeLine(key) {
    set((state) => ({
      lines: state.lines.filter((entry) => entry.key !== key),
      lastAdded: state.lastAdded?.key === key ? null : state.lastAdded,
    }));
  },

  clear() {
    set({
      generation: get().generation + 1,
      lines: [],
      billDiscount: { type: 'none' },
      customerId: null,
      customerName: null,
      customerPhone: null,
      customer: null,
      lastAdded: null,
      customerStateCode: null,
      note: null,
      taxExempt: null,
      taxExemptNote: null,
      lastRejection: null,
    });
  },

  attachCustomer(customer) {
    set({
      customerId: customer?.id ?? null,
      customerName: customer?.name ?? null,
      customerPhone: customer?.phone ?? null,
      customerStateCode: customer?.stateCode ?? null,
      customer: customer
        ? {
            id: customer.id,
            name: customer.name,
            phone: customer.phone,
            visitCount: customer.visitCount ?? null,
            totalSpendPaise: customer.totalSpendPaise ?? null,
            lastVisitAt: customer.lastVisitAt ?? null,
          }
        : null,
    });
  },

  setNote(note) {
    set({ note: note && note.trim() ? note.trim() : null });
  },

  addOpenItem({ name, ratePaise, qtyMilli, pricingInclusive, noTax }) {
    const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    const openItem = { id, name, taxRateId: null };
    const key = lineKey({ productId: OPEN_ITEM_PRODUCT_ID, batchId: null, ratePaise, discountKind: 'none', discountValue: 0, openItem });
    const line: CartLine = {
      key,
      productId: OPEN_ITEM_PRODUCT_ID,
      productName: name,
      sku: OPEN_ITEM_SKU,
      unit: 'PC',
      barcode: null,
      qtyMilli,
      ratePaise,
      mrpPaise: 0,
      discountKind: 'none',
      discountValue: 0,
      discountPerUnit: false,
      taxRateId: noTax.id,
      gstBps: noTax.gstBps,
      cessBps: noTax.cessBps,
      pricingInclusive,
      hsnCode: null,
      allowDecimalQty: false,
      stockMilli: Number.MAX_SAFE_INTEGER,
      batchId: null,
      batchNumber: null,
      isExpiryTracked: false,
      localName: null,
      categoryName: null,
      expiryDate: null,
      openItem,
    };
    set((state) => ({ lines: [...state.lines, line], lastAdded: { key, qtyMilli }, lastRejection: null }));
  },

  setOpenItemTax(key, rate, noTax) {
    const chosen = rate ?? noTax;
    set((state) => ({
      lines: state.lines.map((entry) =>
        entry.key === key && entry.openItem
          ? {
              ...entry,
              taxRateId: chosen.id,
              gstBps: chosen.gstBps,
              cessBps: chosen.cessBps,
              openItem: { ...entry.openItem, taxRateId: rate ? rate.id : null },
            }
          : entry,
      ),
    }));
  },

  setTaxExempt(mode, note = null) {
    set({ taxExempt: mode, taxExemptNote: mode && note && note.trim() ? note.trim() : null });
  },

  reject(message) {
    set({ lastRejection: { message, at: Date.now() } });
  },

  loadLines(lines) {
    set({
      generation: get().generation + 1,
      lines: lines.map((line) => ({ ...line, key: lineKey(line) })),
      billDiscount: { type: 'none' },
      customerId: null,
      customerName: null,
      customerPhone: null,
      customer: null,
      customerStateCode: null,
      note: null,
      taxExempt: null,
      taxExemptNote: null,
      lastAdded: null,
      lastRejection: null,
    });
  },

  loadHeldBill(lines, customer) {
    set({
      lines: lines.map((line) => ({ ...line, key: lineKey(line) })),
      customerId: customer?.id ?? null,
      customerName: customer?.name ?? null,
      customerPhone: customer?.phone ?? null,
      customerStateCode: customer?.stateCode ?? null,
      customer: customer
        ? {
            id: customer.id,
            name: customer.name,
            phone: customer.phone,
            visitCount: customer.visitCount ?? null,
            totalSpendPaise: customer.totalSpendPaise ?? null,
            lastVisitAt: customer.lastVisitAt ?? null,
          }
        : null,
      lastAdded: null,
      lastRejection: null,
    });
  },
}));

// A reload (F5 with a dialog open, a crash, a power cut) must not wipe the bill the
// customer is waiting on. Persist the bill-level cart after every change; ephemeral
// UI (lastAdded, rejections) is intentionally not restored.
if (typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined') {
  let saveTimer: number | undefined;
  useCart.subscribe((state) => {
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => {
      try {
        window.sessionStorage.setItem(
          CART_STORAGE_KEY,
          JSON.stringify({
            lines: state.lines,
            billDiscount: state.billDiscount,
            customerId: state.customerId,
            customerName: state.customerName,
            customerPhone: state.customerPhone,
            customer: state.customer,
            customerStateCode: state.customerStateCode,
            note: state.note,
            taxExempt: state.taxExempt,
            taxExemptNote: state.taxExemptNote,
          }),
        );
      } catch {
        // Storage full or unavailable: the till keeps working in memory.
      }
    }, 150);
  });
}

/** Per-line discount expressed the way `@sp/core` expects it. */
export function lineDiscountOf(line: CartLine): LineDiscount {
  if (line.discountKind === 'percent') {
    return { type: 'percent', bps: line.discountValue };
  }
  if (line.discountKind === 'amount') {
    return { type: 'amount', paise: line.discountValue, ...(line.discountPerUnit ? { perUnit: true } : {}) };
  }
  return { type: 'none' };
}

/**
 * The inverse of `lineDiscountOf`: a stored discount (a held bill's) back into cart-line fields.
 * The per-unit flag must survive the round trip — ₹10 off each of five units is ₹50, and losing
 * the flag turns it into ₹10 off the whole line.
 */
export function cartDiscountFields(
  discount: LineDiscount,
): Pick<CartLine, 'discountKind' | 'discountValue' | 'discountPerUnit'> {
  if (discount.type === 'percent') return { discountKind: 'percent', discountValue: discount.bps, discountPerUnit: false };
  if (discount.type === 'amount') {
    return { discountKind: 'amount', discountValue: discount.paise, discountPerUnit: discount.perUnit === true };
  }
  return { discountKind: 'none', discountValue: 0, discountPerUnit: false };
}

/**
 * Price the cart. Split out of the store so the component can memoise it and so the same
 * maths is directly testable without a React runtime.
 *
 * `roundOffMode` must be the store's configured mode. Guessing it would put the till's
 * number out by up to fifty paise against the invoice it is about to print.
 */
export function priceCart(
  lines: CartLine[],
  billDiscount: BillDiscount,
  interState: boolean,
  roundOffMode: RoundOffMode = 'rupee',
  taxRemoval: TaxRemovalMode | null = null,
): {
  priced: PricedLine[];
  totals: SaleTotals;
  /** "No Tax": what the bill came down by (0 when off). */
  taxRemovedPaise: number;
  /** The part of `totals.billDiscountPaise` that is the No Tax relief, not a discount the cashier gave. */
  taxReliefPaise: number;
} {
  if (lines.length === 0) {
    return { priced: [], totals: computeTotals([], new Map(), roundOffMode), taxRemovedPaise: 0, taxReliefPaise: 0 };
  }

  const ratesById = new Map<string, TaxRate>(lines.map((line) => [line.taxRateId, taxRateOf(line)]));

  // `pricingInclusive` decides whether GST comes out of the rate or is added on top. A
  // supermarket shelf price is almost always MRP-inclusive, so this flag is the difference
  // between a correct receipt and one that is silently short by the tax.
  const base = lines.map((line) =>
    priceLine(
      {
        lineId: line.key,
        productId: line.productId,
        productName: line.productName,
        batchId: line.batchId,
        batchNumber: line.batchNumber,
        qtyMilli: line.qtyMilli,
        ratePaise: line.ratePaise,
        mrpPaise: line.mrpPaise,
        discount: lineDiscountOf(line),
        rate: taxRateOf(line),
        pricingInclusive: line.pricingInclusive,
        hsnCode: line.hsnCode,
      },
      interState,
    ),
  );

  const rateFor = (line: PricedLine): TaxRate => {
    const source = lines.find((candidate) => candidate.key === line.lineId);
    if (!source) throw new Error(`Unknown tax rate for till line ${line.lineId}: refusing to price at 0% GST`);
    return taxRateOf(source);
  };
  const discounted = applyBillDiscount(base, billDiscount, interState, rateFor);
  // The same removal the server applies, after discounts and before the totals.
  const removal = taxRemoval ? removeTax(discounted, taxRemoval, interState, rateFor) : { lines: discounted, removedPaise: 0 };
  const priced = removal.lines;

  return {
    priced,
    totals: computeTotals(priced, taxBucketsFor(priced, ratesById), roundOffMode),
    taxRemovedPaise: removal.removedPaise,
    taxReliefPaise: taxReliefOf(priced),
  };
}